import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions } from "@/hooks/usePermissions";
import { ArrowLeft, Users, TrendingUp, TrendingDown, Crown, Trash2, UserPlus, Share2, Mail, Phone, Copy, MoreHorizontal, Pencil, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import LeaderSelect from "@/components/organizations/LeaderSelect";
import { useTitles } from "@/components/organizations/TitleManager";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

function getOrganizationIdsFromNotes(notes: string | null): string[] {
  if (!notes) return [];
  const lines = notes.split("\n").map((l) => l.trim());
  const idsLine = lines.find(
    (l) => l.startsWith(MINISTRY_IDS_NOTE_PREFIX) || l.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
}

function getOrganizationFromNotes(notes: string | null): string {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
}

function getMemberMinistries(department: string | null | undefined): string[] {
  if (!department) return [];
  return department
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

type MemberOption = { id: string; full_name: string };
type LeaderRow = {
  id: string;
  member_id: string;
  title_id: string | null;
  sort_order: number | null;
  member_name: string;
  member_phone: string | null;
  member_email: string | null;
  title_name: string | null;
};

type OrganizationLeaderRecord = {
  id: string;
  member_id: string;
  title_id: string | null;
  sort_order: number | null;
};

interface TitleAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  inputClassName?: string;
}

function TitleAutocompleteInput({
  value,
  onChange,
  options,
  placeholder,
  inputClassName,
}: TitleAutocompleteInputProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filteredOptions = useMemo(() => {
    const query = value.trim().toLowerCase();
    const uniqueOptions = Array.from(new Set(options.filter(Boolean)));
    if (!query) return uniqueOptions;
    return uniqueOptions.filter((option) => option.toLowerCase().includes(query));
  }, [options, value]);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        className={inputClassName}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Toggle title suggestions"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
      >
        <ChevronDown className="h-4 w-4" />
      </button>

      {open && filteredOptions.length > 0 && (
        <div className="absolute left-0 top-full z-[130] mt-1 w-full max-h-56 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              className="w-full rounded-sm px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MinistryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useChurch();
  const { isChurchAdmin, can } = usePermissions();
  const isAdmin = isChurchAdmin || can("can_manage_organizations");
  const churchId = profile?.id;
  const queryClient = useQueryClient();

  const [showAssign, setShowAssign] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<MemberOption[]>([]);
  const [titleInput, setTitleInput] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [contactTarget, setContactTarget] = useState<LeaderRow | null>(null);
  const [editTarget, setEditTarget] = useState<LeaderRow | null>(null);
  const [editTitleInput, setEditTitleInput] = useState("");

  // Fetch ministry
  const { data: ministry, isLoading: ministryLoading } = useQuery({
    queryKey: ["ministry_detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, description, type, church_id")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch members in this ministry (by department)
  const { data: members = [] } = useQuery({
    queryKey: ["ministry_members", id, ministry?.name, churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, phone, email, status, department")
        .eq("church_id", churchId!);
      if (error) throw error;
      const target = (ministry?.name || "").trim().toLowerCase();
      return (data || [])
        .filter((member: any) => getMemberMinistries(member.department).some((name) => name.toLowerCase() === target))
        .sort((a: any, b: any) => String(a.full_name || "").localeCompare(String(b.full_name || ""), undefined, { sensitivity: "base" }));
    },
    enabled: !!ministry?.name && !!churchId,
  });

  // Fetch leaders
  const { data: leaders = [] } = useQuery<LeaderRow[]>({
    queryKey: ["ministry_leaders", id],
    queryFn: async () => {
      const { data: leaderRows, error } = await (supabase as any)
        .from("organization_leaders")
        .select("id, member_id, title_id, sort_order")
        .eq("organization_id", id!);
      if (error) throw error;
      const typedLeaderRows = (leaderRows || []) as OrganizationLeaderRecord[];
      if (typedLeaderRows.length === 0) return [];

      const memberIds = [...new Set(typedLeaderRows.map((l) => l.member_id))];
      const { data: memberData } = await supabase
        .from("members")
        .select("id, full_name, phone, email")
        .in("id", memberIds);
      const memberMap = new Map((memberData || []).map((m) => [m.id, m]));

      const titleIds = [...new Set(typedLeaderRows.filter((l) => l.title_id).map((l) => l.title_id!))];
      let titleMap = new Map<string, string>();
      if (titleIds.length > 0) {
        const { data: titles } = await supabase
          .from("organization_titles")
          .select("id, name")
          .in("id", titleIds);
        titleMap = new Map((titles || []).map((t) => [t.id, t.name]));
      }

      return typedLeaderRows.map((l) => ({
        id: l.id,
        member_id: l.member_id,
        title_id: l.title_id,
        sort_order: l.sort_order ?? null,
        member_name: memberMap.get(l.member_id)?.full_name || "Unknown",
        member_phone: memberMap.get(l.member_id)?.phone || null,
        member_email: memberMap.get(l.member_id)?.email || null,
        title_name: l.title_id ? titleMap.get(l.title_id) || null : null,
      })).sort((left, right) => {
        const leftOrder = left.sort_order ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.sort_order ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return left.member_name.localeCompare(right.member_name, undefined, { sensitivity: "base" });
      });
    },
    enabled: !!id,
  });

  // Fetch finance records for this ministry
  const { data: financeRecords = [] } = useQuery({
    queryKey: ["ministry_finance", id, ministry?.name],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("id, amount, record_type, category, date, notes")
        .eq("church_id", churchId!);
      if (error) throw error;
      // Filter records that belong to this ministry
      return (data || []).filter((r) => {
        const ids = getOrganizationIdsFromNotes(r.notes);
        if (ids.includes(id!)) return true;
        const orgName = getOrganizationFromNotes(r.notes);
        return orgName === ministry?.name;
      });
    },
    enabled: !!id && !!ministry?.name && !!churchId,
  });

  const effectiveChurchId = ministry?.church_id ?? churchId;
  const { data: titles = [] } = useTitles(effectiveChurchId);

  // Calculations
  const today = new Date();
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const ytdFinanceRecords = financeRecords.filter((record) => {
    const recordDate = new Date(record.date);
    return !Number.isNaN(recordDate.getTime()) && recordDate >= yearStart && recordDate <= today;
  });
  const ytdRangeLabel = `${yearStart.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - ${today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  const totalIncome = ytdFinanceRecords
    .filter((r) => r.record_type === "income")
    .reduce((sum, r) => sum + Number(r.amount), 0);
  const totalExpense = ytdFinanceRecords
    .filter((r) => r.record_type === "expense")
    .reduce((sum, r) => sum + Number(r.amount), 0);

  // Assign leader mutation
  const assignMutation = useMutation({
    mutationFn: async () => {
      if (selectedMembers.length === 0) throw new Error("Select at least one member");

      let titleId: string | null = null;
      const trimmed = titleInput.trim();
      if (trimmed) {
        // Check if an existing title matches (case-insensitive)
        const existing = titles.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
        if (existing) {
          titleId = existing.id;
        } else {
          // Create new title
          const { data: newTitle, error: titleErr } = await supabase
            .from("organization_titles")
            .insert({ name: trimmed, church_id: effectiveChurchId })
            .select("id")
            .single();
          if (titleErr) throw titleErr;
          titleId = newTitle.id;
        }
      }

      const rows = selectedMembers.map((m) => ({
        organization_id: id!,
        member_id: m.id,
        church_id: effectiveChurchId,
        title_id: titleId,
        sort_order: leaders.length + 1,
      }));
      const { error } = await (supabase as any).from("organization_leaders").insert(rows);
      if (error) {
        if (error.message.includes("duplicate")) throw new Error("One or more members already assigned");
        if (error.message.toLowerCase().includes("row-level security")) {
          throw new Error("You do not have permission to assign leaders for this ministry.");
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ministry_leaders", id] });
      queryClient.invalidateQueries({ queryKey: ["organization_titles", effectiveChurchId] });
      toast.success("Leader(s) assigned");
      setSelectedMembers([]);
      setTitleInput("");
      setShowAssign(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateLeaderMutation = useMutation({
    mutationFn: async ({ leaderId, titleId }: { leaderId: string; titleId: string | null }) => {
      const { error } = await supabase
        .from("organization_leaders")
        .update({ title_id: titleId })
        .eq("id", leaderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ministry_leaders", id] });
      queryClient.invalidateQueries({ queryKey: ["organization_titles", effectiveChurchId] });
      toast.success("Leader updated");
      setEditTarget(null);
      setEditTitleInput("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reorderLeadersMutation = useMutation({
    mutationFn: async ({ leaderId, direction }: { leaderId: string; direction: "up" | "down" }) => {
      const currentIndex = leaders.findIndex((leader) => leader.id === leaderId);
      if (currentIndex === -1) return;

      const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (swapIndex < 0 || swapIndex >= leaders.length) return;

      const currentLeader = leaders[currentIndex];
      const swapLeader = leaders[swapIndex];

      const { error: firstError } = await (supabase as any)
        .from("organization_leaders")
        .update({ sort_order: -1 })
        .eq("id", currentLeader.id);
      if (firstError) throw firstError;

      const { error: secondError } = await (supabase as any)
        .from("organization_leaders")
        .update({ sort_order: currentLeader.sort_order ?? currentIndex + 1 })
        .eq("id", swapLeader.id);
      if (secondError) throw secondError;

      const { error: thirdError } = await (supabase as any)
        .from("organization_leaders")
        .update({ sort_order: swapLeader.sort_order ?? swapIndex + 1 })
        .eq("id", currentLeader.id);
      if (thirdError) throw thirdError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ministry_leaders", id] });
      toast.success("Leader order updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Remove leader mutation
  const removeMutation = useMutation({
    mutationFn: async (leaderId: string) => {
      const { error } = await supabase.from("organization_leaders").delete().eq("id", leaderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ministry_leaders", id] });
      toast.success("Leader removed");
      setDeleteTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleShareLeader = async (leader: LeaderRow) => {
    const details = [
      leader.member_email ? `Email: ${leader.member_email}` : null,
      leader.member_phone ? `Phone: ${leader.member_phone}` : null,
      leader.title_name ? `Title: ${leader.title_name}` : null,
      ministry?.name ? `Ministry: ${ministry.name}` : null,
    ].filter(Boolean).join("\n");

    if (!details) {
      toast.error("No contact information available to share.");
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${leader.member_name} Contact`,
          text: details,
        });
        return;
      }

      await navigator.clipboard.writeText(details);
      toast.success("Contact details copied to clipboard.");
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast.error("Unable to share contact details.");
      }
    }
  };

  const handleCopyLeader = async (leader: LeaderRow) => {
    const details = [
      leader.member_name,
      leader.title_name ? `Title: ${leader.title_name}` : null,
      leader.member_email ? `Email: ${leader.member_email}` : null,
      leader.member_phone ? `Phone: ${leader.member_phone}` : null,
    ].filter(Boolean).join("\n");

    try {
      await navigator.clipboard.writeText(details);
      toast.success("Contact details copied to clipboard.");
    } catch {
      toast.error("Unable to copy contact details.");
    }
  };

  const handleEditLeader = (leader: LeaderRow) => {
    setEditTarget(leader);
    setEditTitleInput(leader.title_name ?? "");
  };

  const handleSaveLeaderEdit = async () => {
    if (!editTarget) return;

    const trimmed = editTitleInput.trim();
    let titleId: string | null = null;
    if (trimmed) {
      const existing = titles.find((title) => title.name.toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        titleId = existing.id;
      } else {
        const { data: newTitle, error } = await supabase
          .from("organization_titles")
          .insert({ name: trimmed, church_id: effectiveChurchId })
          .select("id")
          .single();
        if (error) {
          toast.error(error.message);
          return;
        }
        titleId = newTitle.id;
      }
    }

    updateLeaderMutation.mutate({ leaderId: editTarget.id, titleId });
  };

  if (ministryLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-muted-foreground">Loading ministry...</div>
      </div>
    );
  }

  if (!ministry) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-muted-foreground">Ministry not found.</p>
        <Button variant="outline" onClick={() => navigate("/ministries")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Ministries
        </Button>
      </div>
    );
  }

  const statCardClass = "rounded-2xl border border-border bg-card p-5 shadow-sm";

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/ministries")}>
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">{ministry.name}</h1>
        {ministry.description && (
          <p className="text-muted-foreground mt-1">{ministry.description}</p>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={statCardClass}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Members</p>
              <p className="text-2xl font-bold text-foreground">{members.length}</p>
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Income (YTD)</p>
              <p className="text-2xl font-bold text-foreground">${totalIncome.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mt-1">{ytdRangeLabel}</p>
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
              <TrendingDown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Expenses (YTD)</p>
              <p className="text-2xl font-bold text-foreground">${totalExpense.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mt-1">{ytdRangeLabel}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leadership Structure */}
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-heading font-semibold text-foreground">Leadership</h2>
            <Badge variant="secondary" className="text-xs">{leaders.length}</Badge>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowAssign(!showAssign)}>
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Assign Leader</span>
            </Button>
          )}
        </div>

        {/* Assign form */}
        {showAssign && (
          <div className="px-5 py-5 border-b border-border bg-muted/30 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Search Members</label>
                <LeaderSelect
                  churchId={churchId}
                  selected={selectedMembers}
                  onChange={setSelectedMembers}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Title</label>
                <TitleAutocompleteInput
                  value={titleInput}
                  onChange={setTitleInput}
                  options={titles.map((title) => title.name)}
                  placeholder="Type or pick a title..."
                  inputClassName="h-[36px] pr-9"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="ghost" size="sm" onClick={() => { setShowAssign(false); setSelectedMembers([]); setTitleInput(""); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => assignMutation.mutate()} disabled={assignMutation.isPending || selectedMembers.length === 0}>
                {assignMutation.isPending ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </div>
        )}

        {leaders.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            No leaders assigned yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Leader</th>
                  <th className="px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Title</th>
                  <th className="px-5 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leaders.map((leader, index) => (
                  <tr key={leader.id} className="transition-colors hover:bg-secondary/30">
                    <td className="px-5 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary shrink-0">
                          {leader.member_name.split(" ").map((n) => n[0]?.toUpperCase()).join("").slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-foreground">{leader.member_name}</p>
                          {leader.member_phone && (
                            <p className="truncate text-xs text-muted-foreground">{leader.member_phone}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-foreground">
                      {leader.title_name ? (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{leader.title_name}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">No title</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setContactTarget(leader)}
                          title="Contact"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-muted-foreground shrink-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditLeader(leader)}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={index === 0 || reorderLeadersMutation.isPending}
                              onClick={() => reorderLeadersMutation.mutate({ leaderId: leader.id, direction: "up" })}
                            >
                              <ChevronUp className="w-4 h-4 mr-2" /> Move up
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={index === leaders.length - 1 || reorderLeadersMutation.isPending}
                              onClick={() => reorderLeadersMutation.mutate({ leaderId: leader.id, direction: "down" })}
                            >
                              <ChevronDown className="w-4 h-4 mr-2" /> Move down
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteTarget({ id: leader.id, name: leader.member_name })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Leader</DialogTitle>
            <DialogDescription>
              Update the title for {editTarget?.member_name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <TitleAutocompleteInput
              value={editTitleInput}
              onChange={setEditTitleInput}
              options={titles.map((title) => title.name)}
              placeholder="Title (type or pick)"
              inputClassName="h-9 pr-9"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank if this leader should not have a title.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={handleSaveLeaderEdit} disabled={updateLeaderMutation.isPending}>
              {updateLeaderMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contactTarget} onOpenChange={(open) => !open && setContactTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Leader</DialogTitle>
            <DialogDescription>
              {contactTarget?.member_name}
              {contactTarget?.title_name ? ` • ${contactTarget.title_name}` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</p>
                <p className="mt-1 text-sm text-foreground">{contactTarget?.member_email || "No email available"}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Phone</p>
                <p className="mt-1 text-sm text-foreground">{contactTarget?.member_phone || "No phone available"}</p>
              </div>
            </div>

            <div>
              <p className="mb-3 text-sm font-medium text-foreground">Share</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  type="button"
                  className="flex flex-col items-center gap-1.5"
                  onClick={() => contactTarget && handleShareLeader(contactTarget)}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1e3a8a] text-white">
                    <Share2 className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Share</span>
                </button>
                {contactTarget?.member_email && (
                  <button
                    type="button"
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      window.open(`mailto:${contactTarget.member_email}`, "_self");
                    }}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#34a853] text-white">
                      <Mail className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] text-muted-foreground">Email</span>
                  </button>
                )}
                {contactTarget?.member_phone && (
                  <button
                    type="button"
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      window.open(`tel:${contactTarget.member_phone}`, "_self");
                    }}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white">
                      <Phone className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] text-muted-foreground">Call</span>
                  </button>
                )}
                <button
                  type="button"
                  className="flex flex-col items-center gap-1.5"
                  onClick={() => contactTarget && handleCopyLeader(contactTarget)}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-700 text-white">
                    <Copy className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Copy</span>
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setContactTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Leader Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Leader</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteTarget?.name} from the leadership of {ministry.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteTarget && removeMutation.mutate(deleteTarget.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
